import React, { useState } from "react";
import { FaUser, FaLock, FaEnvelope, FaGoogle, FaGithub } from "react-icons/fa";
import { GoogleProvider, auth, GithubProvider, db } from "./firebase";
import { updateProfile } from "firebase/auth";
import {
  collection,
  doc,
  setDoc,
  query,
  where,
  getDocs,
} from "firebase/firestore";

import Logo from "../../assets/logoTrans.png";
import "./login.css";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";

const Login = ({ location }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [isSignUpMode, setIsSignUpMode] = useState(false);
  const [error, setError] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const [passwordError, setPasswordError] = useState(null);

  const navigate = useNavigate();

  const handleSignIn = async (e) => {
    e.preventDefault();

    // Reset previous errors
    setError(null);
    setEmailError(null);
    setPasswordError(null);

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      // Check if the user's email is verified
      if (!userCredential.user.emailVerified) {
        setError(
          "Email not verified. Please check your email for verification."
        );
        // You may want to provide an option for the user to resend the verification email.
        return;
      }
      markUserAsActive(auth.currentUser.uid);

      console.log("User signed in: ", userCredential.user);

      // Authentication successful, you can redirect or perform other actions here
    } catch (error) {
      console.error("Error signing in:", error.message);
      setError("Error signing in. Please check your credentials.");
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();

    // Reset previous errors
    setError(null);
    setEmailError(null);
    setPasswordError(null);

    // Validate email and password
    if (!username) {
      setError("Name is required");
      return;
    }
    if (!email) {
      setEmailError("Email is required");
      return;
    }
    if (!validateEmail(email)) {
      setEmailError("Invalid email format");
      return;
    }
    if (!password) {
      setPasswordError("Password is required");
      return;
    }
    if (password.length < 8) {
      setPasswordError("Password must be at least 8 characters long");
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      // Get the user's name from the form input
      const user = userCredential.user;

      // Generate a unique username
      const uniqueUsername = await generateUniqueUsername(email);

      // Update user's profile with display name
      await updateProfile(user, {
        displayName: username,
      });

      // Add the user to the Firestore "users" collection
      await addUserToFirestore(user.uid, email, uniqueUsername, username);
      console.log("User signed up and added to Firestore: ", user);

      // Check if the user's email is verified for email/password sign-up
      if (!userCredential.user.emailVerified) {
        setError(
          "Email not verified. Please check your email for verification."
        );
        return;
      }

      // Authentication successful, you can redirect or perform other actions here
    } catch (error) {
      if (error.code === "auth/email-already-in-use") {
        setError("Error signing up: Email already in use");
      } else if (error.code === "auth/invalid-email") {
        setError("Error signing up: Invalid email");
      } else if (error.code === "auth/weak-password") {
        setError("Error signing up: Weak password");
      } else {
        setError(`Error signing up: ${error.message}`);
      }
    }
  };

  // Helper function to validate email format
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const generateUniqueUsername = async (email) => {
    const baseUsername = email.split("@")[0];
    let username = `${baseUsername}_${Math.floor(Math.random() * 10000)}`;

    let usernameExists = true;
    while (usernameExists) {
      const querySnapshot = await getDocs(
        query(collection(db, "users"), where("username", "==", username))
      );
      usernameExists = !querySnapshot.empty;
      if (usernameExists) {
        username = `${baseUsername}_${Math.floor(Math.random() * 10000)}`;
      }
    }
    return username;
  };

  const markUserAsActive = async (userId) => {
    const userRef = doc(db, "users", userId);
    await setDoc(userRef, { isOnline: true }, { merge: true });
  };

  const addUserToFirestore = async (uid, email, username, displayName) => {
    await setDoc(doc(db, "users", uid), {
      uid: uid,
      email: email,
      username: username,
      displayName: displayName,
    });
  };

  const handleSignInGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, GoogleProvider);
      await markUserAsActive(result.user.uid);

      // Check if user exists in Firestore
      const userDoc = await getDocs(
        query(collection(db, "users"), where("uid", "==", result.user.uid))
      );
      if (userDoc.empty) {
        // If user does not exist in Firestore, add the user
        const uniqueUsername = await generateUniqueUsername(result.user.email);
        await addUserToFirestore(
          result.user.uid,
          result.user.email,
          uniqueUsername,
          result.user.displayName
        );
      }
    } catch (error) {
      console.error("Error signing in with Google:", error.message);
      if (error.code === "auth/account-exists-with-different-credential") {
        setError(
          "An account with this email already exists with a different sign-in method."
        );
      } else if (error.code === "auth/popup-closed-by-user") {
        setError("Google sign-in popup was closed by the user.");
      } else {
        setError("Error signing in with Google. Please try again later.");
      }
    }
  };

  const handleSignInGithub = async () => {
    try {
      const result = await signInWithPopup(auth, GithubProvider);
      await markUserAsActive(result.user.uid);

      // Check if user exists in Firestore
      const userDoc = await getDocs(
        query(collection(db, "users"), where("uid", "==", result.user.uid))
      );
      if (userDoc.empty) {
        // If user does not exist in Firestore, add the user
        const uniqueUsername = await generateUniqueUsername(result.user.email);
        await addUserToFirestore(
          result.user.uid,
          result.user.email,
          uniqueUsername,
          result.user.displayName
        );
      }
    } catch (error) {
      console.error("Error signing in with GitHub:", error.message);
      if (error.code === "auth/account-exists-with-different-credential") {
        setError(
          "An account with this email already exists with a different sign-in method."
        );
      } else if (error.code === "auth/popup-closed-by-user") {
        setError("GitHub sign-in popup was closed by the user.");
      } else {
        setError("Error signing in with GitHub. Please try again later.");
      }
    }
  };

  const handleResetPassword = () => {
    try {
      navigate("/reset-password");
    } catch (error) {
      console.error("Error navigating to reset-password:", error.message);
      setError("Error navigating to reset password. Please try again later.");
    }
  };

  const toggleMode = () => {
    setIsSignUpMode((prevMode) => !prevMode);
  };

  return (
    <div className={`container ${isSignUpMode ? "sign-up-mode" : ""}`}>
      <div className="forms-container">
        <div className="signin-signup">
          {/* Sign-in Form */}
          <form action="#" className="sign-in-form" onSubmit={handleSignIn}>
            <img src={Logo} alt="Logo" className="w-[250px] mb-8" />
            <h2 className="title">Sign in</h2>
            <div className="input-field">
              <FaEnvelope className="icon" />
              <input
                type="text"
                placeholder="Email"
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="input-field">
              <FaLock className="icon" />
              <input
                type="password"
                placeholder="Password"
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {error && <p className="error-message">{error}</p>}
            {location?.state?.unverifiedUser && (
              <p className="error-message">
                Your email is not verified. Please check your email for
                verification.
              </p>
            )}
            <p
              className="text-[#ff8080] mb-2 mt-2 cursor-pointer"
              onClick={handleResetPassword}
            >
              Forgot password?
            </p>
            <input type="submit" value="Login" className="btn bg-[#9999ff]" />
            <p className="social-text">Or Sign in with social platforms</p>
            <div className="social-media">
              <button className="social-icon" onClick={handleSignInGoogle}>
                <FaGoogle />
              </button>
              <button className="social-icon" onClick={handleSignInGithub}>
                <FaGithub />
              </button>
            </div>
          </form>

          {/* Sign-up Form */}
          <form action="#" className="sign-up-form" onSubmit={handleSignUp}>
            <img src={Logo} alt="Logo" className="w-[250px] mb-8" />
            <h2 className="title">Sign up</h2>
            <div className="input-field">
              <FaUser className="icon" />
              <input
                type="text"
                placeholder="Full Name"
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div className="input-field">
              <FaEnvelope className="icon" />
              <input
                type="email"
                placeholder="Email"
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {emailError && <p className="error-message">{emailError}</p>}
            <div className="input-field">
              <FaLock className="icon" />
              <input
                type="password"
                placeholder="Password"
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
            {passwordError && <p className="error-message">{passwordError}</p>}
            {error && <p className="error-message">{error}</p>}
            <input type="submit" className="btn bg-[#9999ff]" value="Sign up" />
            <p className="social-text">Or Sign up with social platforms</p>
            <div className="social-media">
              <button className="social-icon" onClick={handleSignInGoogle}>
                <FaGoogle />
              </button>
              <button className="social-icon" onClick={handleSignInGithub}>
                <FaGithub />
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Panels Container */}
      <div className="panels-container">
        {/* Left Panel */}
        <div className="panel left-panel">
          <div className="content ">
            <h3 className="mb-2"> New here ?</h3>
            <p className="mb-2 font-medium ">
              Sign Up to unlock a world of seamless collaboration where tasks,
              projects, and communication come together effortlessly. Collab Pro
              is your all-in-one collaboration platform, designed to elevate
              teamwork, streamline projects, and enhance communication.
            </p>
            <button className="btn-trans " onClick={toggleMode}>
              Sign up
            </button>
          </div>
        </div>

        {/* Right Panel */}
        <div className="panel right-panel">
          <div className="content">
            <h3 className="mb-2">One of us ?</h3>
            <p className="mb-2 font-medium ">
              Sign Up to unlock a world of seamless collaboration where tasks,
              projects, and communication come together effortlessly. Collab Pro
              is your all-in-one collaboration platform, designed to elevate
              teamwork, streamline projects, and enhance communication.
            </p>
            <button className="btn-trans" onClick={toggleMode}>
              Sign in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
